import React, { useEffect, useRef } from "react";
import { Button } from "@progress/kendo-react-buttons";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import FormTextField from "../../components/formFields/FormTextField";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import {
  createRoomType,
  getAllActiveRoomTypes,
  getAllRoomTypes,
  getRoomTypeByID,
  updateRoomType,
} from "./services/roomType.services";
import { clearRoomTypeDetails } from "./roomTypeSlice";
import { closed } from "../../components/drawer/drawerSlice";
import { Typography } from "@progress/kendo-react-common";
import { RxCross1 } from "react-icons/rx";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { STATUSARRAY } from "../../_contstants/common";
import IconButton from "../../components/common/IconButton";

const CreateRoomType: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);

  const RoomTypeDetail = useAppSelector(
    (state) => state.roomType.RoomTypeDetail
  );
  const RoomTypeID = useAppSelector((state) => state.drawer.data);
  const formLoading = useAppSelector((state) => state.roomType.formLoading);
  const isIndirect = useAppSelector((state) => state.drawer.isIndirect);

  const [formKey, setFormKey] = React.useState(1);

  useEffect(() => {
    if (RoomTypeID) {
      dispatch(getRoomTypeByID(RoomTypeID));
    }
  }, [RoomTypeID]);

  useEffect(() => {
    return () => {
      dispatch(clearRoomTypeDetails());
      dispatch(closed());
    };
  }, []);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [RoomTypeDetail]);

  const handleSubmit = async (values: any) => {
    try {
      if (!RoomTypeID) {
        const insertPayload = {
          RoomType: values.RoomType,
          Prefix: values.Prefix,
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(createRoomType(insertPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearRoomTypeDetails());
          if (isIndirect) {
            dispatch(getAllActiveRoomTypes());
          } else {
            dispatch(getAllRoomTypes());
          }
        }
      } else {
        const editPayload = {
          ID: RoomTypeID,
          RoomType: values.RoomType,
          Prefix: values.Prefix,
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(updateRoomType(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearRoomTypeDetails());
          dispatch(getAllRoomTypes());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  const handleFormClose = () => {
    dispatch(closed());
  };

  return (
    <>
      {formLoading && <LoadingPanel gridRef={gridRef} />}
      <Form
        key={formKey}
        onSubmit={handleSubmit}
        initialValues={RoomTypeDetail}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement style={{ width: "100%" }}>
            <GridLayout
              style={{
                display: "flex",
                flexWrap: "wrap",
              }}
              gap={{ rows: 10 }}
              cols={[{ width: "100%" }]}
            >
              <GridLayoutItem className="master-drawer-header">
                <Typography.h4 className="m-0">
                  {RoomTypeID ? "Update Room Type" : "Add Room Type"}
                </Typography.h4>
                <IconButton
                  type="button"
                  fillMode={"flat"}
                  themeColor={"dark"}
                  style={{ marginRight: 4 }}
                  onClick={() => dispatch(closed())}
                >
                  <RxCross1 />
                </IconButton>
              </GridLayoutItem>

              <GridLayoutItem style={{ width: "100%" }}>
                <Field
                  name="RoomType"
                  label="Room Type"
                  placeholder="Room Type"
                  component={FormTextField}
                  validator={requiredValidator}
                  astrike={true}
                />
              </GridLayoutItem>
              <GridLayoutItem style={{ width: "100%" }}>
                <Field
                  name="Prefix"
                  label="Prefix"
                  placeholder="Prefix"
                  component={FormTextField}
                  // validator={requiredValidator}
                  // astrike={true}
                />
              </GridLayoutItem>

              <GridLayoutItem style={{ width: "100%" }}>
                <Field
                  id={"IsActive"}
                  name={"IsActive"}
                  label={"Status"}
                  component={FormSelectionField}
                  validator={requiredValidator}
                  astrike={true}
                  placeholder="i.e. Active"
                  options={STATUSARRAY?.map((status: any) => {
                    return {
                      value: status.value,
                      label: status.label,
                    };
                  })}
                />
              </GridLayoutItem>

              <GridLayoutItem
                colSpan={3}
                style={{
                  textAlign: "end",
                  marginTop: "20px",
                  width: "100%",
                }}
              >
                <ButtonWithLoading
                  label={RoomTypeID ? "Update" : "Save"}
                  type="submit"
                  disabled={!formRenderProps.allowSubmit || formLoading}
                  loading={formLoading}
                />

                <Button
                  type="button"
                  fillMode={"outline"}
                  themeColor={"primary"}
                  style={{ marginLeft: 10 }}
                  onClick={handleFormClose}
                >
                  Cancel
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </FormElement>
        )}
      />
    </>
  );
};

export default CreateRoomType;
